import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  WithdrawalType as WithdrawalTypeEnum,
  Withdrawal as WithdrawalType,
} from "../../types/withdrawal.type";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import dayjs from "dayjs";
import { Partner } from "../../types/partner.type";
import { useExchangeRate } from "../../contexts/exchangeRate.context";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function Withdrawals() {
  const { itemId, name } = useParams();
  const { exchangeRate } = useExchangeRate();
  const [withdrawals, setWithdrawals] = useState<WithdrawalType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { data: cashboxesList, loading: cashboxesLoading } =
    useGetRequest("/cashbox/list");
  const { data: partnersList, loading: partnersListLoading } =
    useGetRequest("/partners/list");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/partners/${itemId}/withdrawals`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });
      setWithdrawals(response.data.withdrawals);
      setTotal(response.data.total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение выводов.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/partners/${itemId}/withdraw`, data);
      message.success("Вывод успешно добавлена.");
      form.resetFields();
    } catch (error: any) {
      console.error("Error saving withdrawal:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchWithdrawals();
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [page, pageSize, dateRange]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
        await api.delete(`/partners/${id}/withdrawal`);
      message.success("Вывод успешно удален.");
      fetchWithdrawals();
    } catch (error: any) {
      console.error("Error deleting withdrawal:", error);
      message.error(error.response.data.error);
    } finally {
      setDeletingId(null);
    }
  };


  const columns = [
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Партнер",
      dataIndex: "partnerName",
      key: "partnerName",
    },
    {
      title: "Касса",
      dataIndex: "cashboxName",
      key: "cashboxName",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Пользователь",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Действия",
      dataIndex: "actions",
      key: "actions",
      render: (text: string, record: WithdrawalType) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => handleDelete(record._id)}
            loading={deletingId === record._id}
          >
            Удалить
          </Button>
        </Space>
      ),
    },
    ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Выводы {name}</Title>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Добавить
        </Button>
      </Space>
      <Table
        loading={loading}
        columns={columns}
        dataSource={withdrawals}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title="Добавить вывод"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="type"
            label="Тип"
            rules={[{ required: true, message: "Выберите тип" }]}
          >
            <Select placeholder="Выберите тип">
              {Object.values(WithdrawalTypeEnum).map((item) => (
                <Select.Option key={item} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите Кассу" loading={cashboxesLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Select.Option key={cashbox._id} value={cashbox._id}>
                  {cashbox.name} | {cashbox.currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Пожалуйста, введите сумму" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[
              { required: true, message: "Пожалуйста, введите описание" },
            ]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
